import { ISelectionGeometryMessage, SelectionInteraction } from "@kuika/kuika-cl-model";
import React, { useEffect, useRef, useState } from "react";
import {
  getCanvasIframe,
  getFrameTransform,
  getIframeDocument,
  getSafeRect,
  getScrollFrame,
  isDeviceSwitching,
  toRect
} from "./canvas-frame";
import {
  BarMove,
  BarSide,
  clampX,
  clampY,
  classifyBarMove,
  compensateScroll,
  IFrameTransform,
  IPlacement,
  IRect,
  IScrollReach,
  ISize,
  placeOverlay,
  scrollReach,
  toParentRect
} from "./geometry";
import { selectionGeometryStore } from "./selection-geometry-store";

/** Distance between the selection outline and the bar. */
const OVERLAY_GAP: number = 8;
/** Slack a newly-fitting bar side must clear before the bar leaves its current side. */
const HYSTERESIS_BAND: number = 12;

/** Where a surface that is not placed sits: far off-screen, still laid out so it can be measured. */
export const PARKED_POSITION: number = -10000;

/**
 * What the overlay RENDERS from: which mode and side the bar is in, whether the bar / edge chip are on
 * screen, and the safe area. The bar's and chip's pixel positions are not here — the placement loop writes
 * them straight to the DOM every frame, so tracking a scroll re-renders nothing.
 */
export interface IOverlayLayout {
  placement: IPlacement;
  /** The off-screen chip, re-centred for its measured (text-dependent) width. Null unless off-screen. */
  chip: IRect | null;
  /** The canvas region the overlay stays inside, for the ⋯ menu's height cap. */
  safe: IRect;
}

export interface IOverlayElements {
  /** The bar as rendered (the breadcrumb segment is always merged into it). */
  bar: React.RefObject<HTMLElement>;
  chip: React.RefObject<HTMLElement>;
}

const ZERO: ISize = { width: 0, height: 0 };

const sizeOf = (element: HTMLElement | null): ISize =>
  element ? { width: element.offsetWidth, height: element.offsetHeight } : ZERO;

const round = (r: IRect | null): string =>
  r ? `${Math.round(r.x)},${Math.round(r.y)},${Math.round(r.width)},${Math.round(r.height)}` : "-";

/** The structural identity of a layout: a frame that only moves the bar changes none of it. */
const layoutKey = (layout: IOverlayLayout | null): string => {
  if (!layout) {
    return "null";
  }
  const p: IPlacement = layout.placement;
  return [
    p.mode,
    p.barSide,
    p.bar === null ? "parked" : "placed",
    layout.chip === null ? "-" : "chip",
    round(layout.safe)
  ].join("|");
};

/** The transform that puts a surface at `rect` (or parks it). Whole pixels: a fractional one blurs text. */
export function surfaceTransform(rect: IRect | null): string {
  const x: number = rect ? Math.round(rect.x) : PARKED_POSITION;
  const y: number = rect ? Math.round(rect.y) : PARKED_POSITION;
  return `translate3d(${x}px, ${y}px, 0)`;
}

/** Writes `transform` on a surface; returns whether it moved. */
function moveSurface(surface: HTMLElement | null, transform: string): boolean {
  if (!surface || surface.style.transform === transform) {
    return false;
  }
  surface.style.transform = transform;
  return true;
}

/** Eased only for a `switch`; see `classifyBarMove`. Removed again once the transition is over. */
const EASING_CLASS: string = "kuika-selection-overlay__surface--easing";
/** A little longer than the transition itself (`--dur-fast`), so the class outlives it. */
const EASING_MS: number = 200;

interface IFrameInputs {
  anchor: IRect;
  visible: IRect;
}

function projectGeometry(
  geometry: ISelectionGeometryMessage,
  rect: IRect,
  transform: IFrameTransform,
  scrollFrame: HTMLElement | null
): IFrameInputs {
  // The agent measured at its own scroll position; the frame may have scrolled on since. Re-project with
  // the live offsets so the overlay tracks the component between messages.
  const liveTop: number = scrollFrame ? scrollFrame.scrollTop : geometry.scrollTop;
  const liveLeft: number = scrollFrame ? scrollFrame.scrollLeft : geometry.scrollLeft;
  return {
    anchor: toParentRect(
      compensateScroll(rect, geometry.scrollTop, geometry.scrollLeft, liveTop, liveLeft, geometry.isFixed),
      transform
    ),
    visible: toParentRect(toRect(geometry.visibleRect), transform)
  };
}

/** Frames in a row with an unchanged position after which the placement loop goes idle. */
const STABLE_FRAMES_TO_IDLE: number = 3;
/**
 * How long the loop keeps ticking after the last scroll or wheel in the canvas. A scroll's events can
 * arrive a frame apart from its paints; ticking through the gap keeps the bar glued to the component
 * instead of catching up in steps.
 */
const SCROLL_TAIL_MS: number = 200;
/**
 * While idle, how often the canvas frame's own position is re-checked. Zoom (a transform on the iframe)
 * and panel resizes move the canvas without any event the overlay could listen to.
 */
const IDLE_WATCH_MS: number = 250;

/** The canvas's on-screen box and scale: when it moves while the loop is idle, the loop wakes. */
const frameSignature = (isReportDesigner: boolean): string => {
  const iframe: HTMLIFrameElement | null = getCanvasIframe();
  if (!iframe) {
    return "-";
  }
  const transform: IFrameTransform = getFrameTransform(iframe);
  return [
    Math.round(transform.frameLeft),
    Math.round(transform.frameTop),
    transform.scale.toFixed(3),
    round(getSafeRect(isReportDesigner))
  ].join("|");
};

/** Whether a later frame could still place differently without any new event. */
type PlacePass = "settled" | "unsettled";

/**
 * Positions the bar and the off-screen chip for the current selection while `active`.
 *
 * The loop runs on animation frames only while something is moving: it goes idle once the position has
 * been the same for a few frames (never within `SCROLL_TAIL_MS` of a scroll or wheel in the canvas), and
 * wakes on a new geometry message, a scroll or wheel in the canvas document, a window resize, a size change
 * of the overlay's surfaces or the canvas, and a moved canvas frame (zoom, panels — checked every
 * `IDLE_WATCH_MS`). The component's rect and scroll offsets are read LIVE, so the bar follows a scroll 1:1.
 *
 * Positions are written to the DOM (`transform`), not React state: React re-renders only when the layout's
 * STRUCTURE changes (`layoutKey`). A side switch is eased; tracking never is (`classifyBarMove`).
 */
export function useOverlayPlacement(
  geometry: ISelectionGeometryMessage | null,
  active: boolean,
  isReportDesigner: boolean,
  elements: IOverlayElements
): IOverlayLayout | null {
  const [layout, setLayout] = useState<IOverlayLayout | null>(null);
  const prevBarSideRef: React.MutableRefObject<BarSide | null> = useRef<BarSide | null>(null);
  const prevPlacementRef: React.MutableRefObject<IPlacement | null> = useRef<IPlacement | null>(null);
  const lastKeyRef: React.MutableRefObject<string> = useRef<string>("null");

  const selectionKey: string | null = geometry?.kuikaId ?? null;
  const interaction: SelectionInteraction | null = geometry?.interaction ?? null;
  useEffect((): void => {
    // A new selection — or the same one after a drag or resize, which may have moved it anywhere — starts
    // fresh: the old side must not bias the new placement, and the bar must not ease in from where it was.
    prevBarSideRef.current = null;
    prevPlacementRef.current = null;
  }, [selectionKey, interaction]);

  useEffect((): (() => void) | undefined => {
    let easingTimer: number = 0;

    /** Returns whether anything visible changed. */
    const commit = (next: IOverlayLayout | null): boolean => {
      const bar: HTMLElement | null = elements.bar.current;
      const chipEl: HTMLElement | null = elements.chip.current;
      const move: BarMove | null = next ? classifyBarMove(prevPlacementRef.current, next.placement) : null;
      prevPlacementRef.current = next?.placement ?? null;

      const barTransform: string = surfaceTransform(next?.placement.bar ?? null);
      const isBarMoving: boolean = bar !== null && bar.style.transform !== barTransform;
      if (bar && isBarMoving) {
        // The easing class goes on before the new transform, so the transition applies to this change.
        window.clearTimeout(easingTimer);
        bar.classList.toggle(EASING_CLASS, move === "switch");
        if (move === "switch") {
          easingTimer = window.setTimeout((): void => bar.classList.remove(EASING_CLASS), EASING_MS);
        }
      }
      const isBarMoved: boolean = moveSurface(bar, barTransform);
      const isChipMoved: boolean = moveSurface(chipEl, surfaceTransform(next?.chip ?? null));
      const moved: boolean = isBarMoved || isChipMoved;

      const key: string = layoutKey(next);
      if (key !== lastKeyRef.current) {
        lastKeyRef.current = key;
        setLayout(next);
        return true;
      }
      return moved;
    };

    if (!active) {
      commit(null);
      return undefined;
    }

    const place = (): PlacePass => {
      const current: ISelectionGeometryMessage | null = selectionGeometryStore.getSnapshot();
      if (!current || !current.rect) {
        // Nothing to place until the agent sends a selection — which wakes the loop.
        commit(null);
        return "settled";
      }
      const iframe: HTMLIFrameElement | null = getCanvasIframe();
      const safe: IRect | null = getSafeRect(isReportDesigner);
      // Mid device-switch animation the iframe is being resized; any rect read now is wrong, and the
      // switch ends without an event of its own — keep looking until it is over.
      if (!iframe || !safe || isDeviceSwitching(iframe)) {
        commit(null);
        return "unsettled";
      }
      const transform: IFrameTransform = getFrameTransform(iframe);
      const scrollFrame: HTMLElement | null = getScrollFrame(getIframeDocument(iframe), isReportDesigner);
      const inputs: IFrameInputs = projectGeometry(current, toRect(current.rect), transform, scrollFrame);
      // Only the report designer: its one scroller is the frame read here, and its header/footer bands overlay
      // the page. A UI screen can scroll somewhere else (#masterRootDI, a table's own scroller), so "the frame
      // cannot scroll there" would not mean "nothing can".
      const reach: IScrollReach | undefined =
        isReportDesigner && scrollFrame !== null && !current.isFixed
          ? scrollReach(scrollFrame.scrollTop, scrollFrame.scrollHeight, scrollFrame.clientHeight, transform.scale)
          : undefined;

      const placement: IPlacement = placeOverlay({
        anchor: inputs.anchor,
        visible: inputs.visible,
        safe,
        bar: sizeOf(elements.bar.current),
        gap: OVERLAY_GAP,
        prevBarSide: prevBarSideRef.current,
        hysteresisBand: HYSTERESIS_BAND,
        reach
      });
      prevBarSideRef.current = placement.barSide;

      let chip: IRect | null = null;
      if (placement.edgeChip) {
        const chipSize: ISize = sizeOf(elements.chip.current);
        const centerX: number = placement.edgeChip.x + placement.edgeChip.width / 2;
        const centerY: number = placement.edgeChip.y + placement.edgeChip.height / 2;
        chip = {
          x: clampX(centerX - chipSize.width / 2, chipSize.width, safe),
          y: clampY(centerY - chipSize.height / 2, chipSize.height, safe),
          width: chipSize.width,
          height: chipSize.height
        };
      }

      return commit({ placement, chip, safe }) ? "unsettled" : "settled";
    };

    let frameId: number = 0;
    let isRunning: boolean = false;
    let stableFrames: number = 0;
    let lastScrollAt: number = -Infinity;
    let lastSignature: string = frameSignature(isReportDesigner);

    const setMoving = (isMoving: boolean): void => {
      const value: string = isMoving ? "transform" : "";
      [elements.bar.current, elements.chip.current].forEach((surface: HTMLElement | null): void => {
        if (surface) {
          surface.style.willChange = value;
        }
      });
    };

    const tick = (): void => {
      const pass: PlacePass = place();
      stableFrames = pass === "settled" ? stableFrames + 1 : 0;
      const isScrolling: boolean = performance.now() - lastScrollAt < SCROLL_TAIL_MS;
      if (stableFrames >= STABLE_FRAMES_TO_IDLE && !isScrolling) {
        isRunning = false;
        setMoving(false);
        lastSignature = frameSignature(isReportDesigner);
        return;
      }
      frameId = requestAnimationFrame(tick);
    };

    const wake = (): void => {
      stableFrames = 0;
      if (!isRunning) {
        isRunning = true;
        setMoving(true);
        frameId = requestAnimationFrame(tick);
      }
    };

    // A scroll or wheel in the canvas: place in THIS frame (not the next one) and keep ticking through the
    // scroll's tail, so the bar moves with the content rather than a frame behind it.
    const onCanvasScroll = (): void => {
      lastScrollAt = performance.now();
      stableFrames = 0;
      if (isRunning) {
        return;
      }
      isRunning = true;
      setMoving(true);
      place();
      frameId = requestAnimationFrame(tick);
    };

    // Size changes of the overlay's own surfaces (a quick prop folds, a chip's value widens) and of the
    // canvas region re-place the bar.
    const resizeObserver: ResizeObserver | null =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver((): void => wake());
    const observed: Set<Element> = new Set<Element>();
    const observeSurfaces = (): void => {
      if (!resizeObserver) {
        return;
      }
      const surfaces: (Element | null)[] = [elements.bar.current, elements.chip.current, getCanvasIframe()];
      // Observe exactly the current surfaces: a replaced canvas iframe or a remounted bar is dropped, not
      // kept alive (and observed) for the life of the overlay.
      observed.forEach((element: Element): void => {
        if (!surfaces.includes(element)) {
          resizeObserver.unobserve(element);
          observed.delete(element);
        }
      });
      surfaces.forEach((surface: Element | null): void => {
        if (surface && !observed.has(surface)) {
          observed.add(surface);
          resizeObserver.observe(surface);
        }
      });
    };

    // Scrolls inside the canvas document (capture: any scroller) — the agent's own geometry messages for
    // them lag a frame behind.
    let scrollDoc: Document | null = null;
    const attachScroll = (): void => {
      const doc: Document | null = getIframeDocument(getCanvasIframe());
      if (doc === scrollDoc) {
        return;
      }
      scrollDoc?.removeEventListener("scroll", onCanvasScroll, true);
      scrollDoc?.removeEventListener("wheel", onCanvasScroll, true);
      scrollDoc = doc;
      scrollDoc?.addEventListener("scroll", onCanvasScroll, true);
      scrollDoc?.addEventListener("wheel", onCanvasScroll, { capture: true, passive: true });
    };

    const watch: number = window.setInterval((): void => {
      observeSurfaces();
      attachScroll();
      if (!isRunning && frameSignature(isReportDesigner) !== lastSignature) {
        wake();
      }
    }, IDLE_WATCH_MS);

    const unsubscribe: () => void = selectionGeometryStore.subscribe(wake);
    window.addEventListener("resize", wake);
    observeSurfaces();
    attachScroll();
    wake();

    return (): void => {
      cancelAnimationFrame(frameId);
      window.clearTimeout(easingTimer);
      isRunning = false;
      setMoving(false);
      window.clearInterval(watch);
      unsubscribe();
      window.removeEventListener("resize", wake);
      scrollDoc?.removeEventListener("scroll", onCanvasScroll, true);
      scrollDoc?.removeEventListener("wheel", onCanvasScroll, true);
      resizeObserver?.disconnect();
    };
  }, [active, isReportDesigner, elements]);

  return layout;
}
